import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, // Uncommented - adding phone support back
  Mail, 
  MapPin, 
  Clock,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Lock,
  Globe,
  CreditCard
} from 'lucide-react';
// Using Cloudinary hosted white logo for footer
const mainLogo = 'https://res.cloudinary.com/dyz8kppso/image/upload/v1756245029/firstcapital-white_meols9.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Personal Banking', path: '/personal-banking' },
    { name: 'Business Banking', path: '/business-banking' },
    { name: 'Loans & Mortgages', path: '/loans-info' },
    { name: 'Online Banking', path: '/online-banking' },
    { name: 'Customer Support', path: '/contact' },
    { name: 'Branch Locations', path: '/contact#locations' }
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
    { name: 'Security Center', path: '/security' },
    { name: 'Accessibility', path: '/accessibility' },
    { name: 'Site Map', path: '/sitemap' },
    { name: 'Disclosures', path: '/disclosures' }
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Youtube, href: '#', label: 'YouTube' }
  ];

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Bank Information with Colored Logo */}
          <div className="col-span-1 lg:col-span-2">
            <div className="flex items-center space-x-4 mb-10">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                className="relative p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl"
              >
                <img 
                  src={mainLogo}
                  alt="First Capital International"
                  className="h-20 w-auto filter drop-shadow-lg"
                />
                {/* Enhanced glow effect with company colors */}
                <motion.div 
                  animate={{ 
                    opacity: [0.2, 0.5, 0.2],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{ 
                    duration: 5, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute inset-0 bg-gradient-to-br from-primary-400/30 via-heritage-400/20 to-primary-400/20 rounded-2xl blur-lg -z-10"
                />
              </motion.div>
            </div>
            
            <p className="text-slate-300 text-lg leading-relaxed mb-8">
              Your trusted financial partner offering comprehensive banking services, loans, and investment solutions. 
              We're committed to helping our clients achieve their financial goals with personalized service and innovative solutions.
            </p>

            {/* Contact Information */}
            <div className="space-y-4">
              {/* Commented out phone number - using email only */}
              {/*
              {/* Commented out phone number - using email only */}
              {/*
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary-400" />
                <span className="text-slate-200">+1 (281) 317-3649</span>
              </div>
              */}
              */}
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary-400" />
                <span className="text-slate-200">+12813173649</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary-400" />
                <span className="text-slate-200">admin@firstcapitalsint.com</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary-400" />
                <span className="text-slate-200">support@firstcapitalsint.com</span>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-primary-400" />
                <span className="text-slate-200">Email Support: Always Available</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-300 hover:text-primary-400 transition-colors duration-200 flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform duration-200">
                      {link.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">Legal & Resources</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-300 hover:text-primary-400 transition-colors duration-200 flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform duration-200">
                      {link.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Social Media */}
            <div className="mt-8">
              <h4 className="text-base font-semibold mb-4 text-white">Follow Us</h4>
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-primary-400 hover:bg-slate-700 transition-all duration-200"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security & Trust Indicators */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0">
            {/* Security Features */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start space-x-8">
              <div className="flex items-center space-x-2">
                <Lock className="w-5 h-5 text-primary-400" />
                <span className="text-slate-300 text-sm">256-bit SSL Encryption</span>
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="w-5 h-5 text-primary-400" />
                <span className="text-slate-300 text-sm">FDIC Insured</span>
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="w-5 h-5 text-primary-400" />
                <span className="text-slate-300 text-sm">Equal Housing Lender</span>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="flex items-center space-x-4">
              <span className="text-slate-400 text-sm">We Accept:</span>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-6 bg-gradient-to-r from-blue-600 to-blue-800 rounded flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-white" />
                </div>
                <div className="w-10 h-6 bg-gradient-to-r from-red-600 to-red-800 rounded flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-white" />
                </div>
                <div className="w-10 h-6 bg-gradient-to-r from-accent-500 to-accent-600 rounded flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-slate-400 text-sm text-center md:text-left">
              <p>© {currentYear} First Capital International. All rights reserved.</p>
              <p className="mt-1">
                Member FDIC | Equal Housing Lender | NMLS ID: 123456
              </p>
            </div>
            <div className="text-slate-400 text-sm">
                              <span>firstcapitalsint.com</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 